import { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Compass } from 'lucide-react';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { routes } from './app/routes';
import { RouteGuard } from './app/RouteGuard';
import Layout from './app/Layout';
import { Button, EmptyState, RouteLoading, Spinner } from './components/ds-dlp';
import { ErrorBoundary } from './components/feedback/ErrorBoundary';
import Login from './pages/Login';
import SetupPage from './pages/SetupPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import LicenseRequired from './pages/LicenseRequired';
import { licenseBlocks } from './api/license';
import { Perm } from './auth/permissions';

// Paths that are not pages: the bare root and the sign-in redirect target
// (api/client.ts sends an expired session to /login). Once signed in, both
// land on the first page the user may open.
const LANDING_PATHS = new Set(['/', '', '/login']);

/** The `*` route (cascade plan 3.5): an unknown path inside the shell. */
function NotFound({ home }: { home: string }) {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <EmptyState
        icon={<Compass />}
        title="Page not found"
        description="This address doesn't match any page. It may have moved, or the link may be mistyped."
        action={<Button onClick={() => navigate(home)}>Go to the start page</Button>}
        headingLevel={2}
      />
    </div>
  );
}

function AppContent() {
  const { isLoading, isAuthenticated, needsSetup, recheckSetup, hasPermission, permissionsLoaded, license } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Default landing page after login: first nav-visible route the user can
  // reach. Avoids hard-coding /dashboard for users who lack the perm.
  const landing = routes.find((r) => r.navLabel && (r.perm == null || hasPermission(r.perm)))?.path ?? '/dashboard';
  useEffect(() => {
    if (isAuthenticated && LANDING_PATHS.has(location.pathname)) navigate(landing, { replace: true });
  }, [isAuthenticated, location.pathname, landing, navigate]);

  // On-prem licence gate: while the installation is not licensed, the
  // server refuses console requests (403 LICENSE_REQUIRED). An administrator
  // is taken to Settings > License to activate a key; anyone else sees a
  // page asking them to contact one.
  const licenseBlocked = isAuthenticated && licenseBlocks(license);
  const canActivate = hasPermission(Perm.SettingsManage);
  useEffect(() => {
    if (!licenseBlocked || !canActivate) return;
    if (location.pathname !== '/settings' || new URLSearchParams(location.search).get('tab') !== 'license') {
      navigate('/settings?tab=license', { replace: true });
    }
  }, [licenseBlocked, canActivate, location.pathname, location.search, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-3 bg-[var(--d2d-bg-primary)] text-dense text-[var(--d2d-text-muted)]" role="status">
        <Spinner size={24} aria-hidden="true" />
        Loading workspace…
      </div>
    );
  }

  if (needsSetup) return <SetupPage onComplete={recheckSetup} />;
  // Password reset works signed out; the emailed link may also be opened
  // where someone is signed in, so its page comes before the shell.
  if (location.pathname === '/reset-password') return <ResetPassword />;
  if (!isAuthenticated) return location.pathname === '/forgot-password' ? <ForgotPassword /> : <Login />;
  if (licenseBlocked && permissionsLoaded && !canActivate) return <LicenseRequired />;

  return (
    <Layout>
      {/* Per page (cascade plan 5.3): a crash shows an error card in the shell
          instead of blanking the app, and the key resets it on navigation. A
          lazy page loads in its own shape (RouteLoading). */}
      <ErrorBoundary key={location.pathname}>
        <Suspense fallback={<RouteLoading />}>
          <Routes>
            {routes.map((spec) => {
              const Page = spec.element;
              return (
                <Route
                  key={spec.path}
                  path={spec.path}
                  element={
                    <RouteGuard perm={spec.perm}>
                      <Page />
                    </RouteGuard>
                  }
                />
              );
            })}
            {/* The landing redirect above handles / and /login; anything else unknown is a not-found page. */}
            <Route path="*" element={LANDING_PATHS.has(location.pathname) ? null : <NotFound home={landing} />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </Layout>
  );
}

function App() {
  // The theme, toasts and dialogs are mounted around App in index.tsx.
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
